import { Accordion } from '@domgen/base-design-system-2.0/atoms/Accordion';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { RegBenefits } from '@domgen/base-design-system-2.0/atoms/RegBenefits';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { useQuery } from '@domgen/base-design-system-2.0/hooks/useQuery';
import { SlidingPanel } from '@domgen/base-design-system-2.0/molecules/SlidingPanel';
import {
  CallUsLandingPageVariant,
  isCallUsLandingPageVariant,
  PosthogFeatureFlags,
} from '@domgen/shared/enums/featureFlags';
import { ModelNumberLookupResponse } from '@domgen/shared/models/services/api/applianceLookup';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { useFeatureFlagEnabled, usePostHog } from '@posthog/react';
import { useEffect, useState } from 'react';
import { CircleTickSolid } from '../../../assets/icons/Tickbox';
import { GiftMessage } from '../../../components/GiftMessage';
import { ModelNumberLocation } from '../../../components/ModelNumberLocation/ModelNumberLocation';
import {
  getModelNumberLocationOptions,
  modelNumberLocationContent,
  modelNumberLocationOptions,
} from '../../../constants/modelNumberLocation';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { StoreEvent } from '../../../enums/storeEvent';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { queryModelNumber } from '../../../services/api/queryModelNumber';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useAppState } from '../../../store/selectors/useAppState';
import { useApplianceFormData } from '../../../store/selectors/useApplianceFormData';
import { useApplianceLookupResults } from '../../../store/selectors/useApplianceLookupResults';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { store } from '../../../store/store';
import { getModelSerialExamples } from '../../../utils/form/modelSerialExamples';
import { formValidators } from '../../../utils/form/validators';
import { getSystemCode } from '../../../utils/getSystemCode';
import { getRegistriaUrl } from '../../../utils/registria/getRegistriaUrl';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { ModelSerialLandingCallUs } from './ModelSerialLandingCallUs';
import type { ModelSerialLandingFormProps } from './ModelSerialLandingOnlineForm.types';
import { ModelSerialLandingOnlineFormDefault } from './ModelSerialLandingOnlineFormDefault';
import { landingBenefits } from './landingBenefits';

const styles = {
  heroSection: 'relative w-full bg-cover bg-center py-space-700 px-space-500 flex justify-center',
  heroOverlay: 'absolute inset-0 bg-black/30',
  heroContent: 'relative z-10 flex justify-center w-full',
  heroBox:
    'bg-(--color-bg-main-container) rounded-radius-200 p-space-700 flex flex-col gap-space-400 md:gap-space-700 w-3xl shadow-lg',
  heroRow: 'flex flex-col md:flex-row gap-space-700 w-full',
  heroLeft: 'flex flex-col gap-space-400 flex-1',
  heroRight: 'flex flex-col gap-space-400 flex-1',
  defaultCtaRow: 'flex justify-center items-center',
  defaultCtaContainer: 'w-1/2 max-w-146',
  canadaLink: 'text-center',
  benefitsSection: 'w-full',
  protectionSection: 'w-full bg-(--color-bg-main) py-space-700 px-space-500',
  protectionContent: 'max-w-6xl mx-auto flex flex-col md:flex-row gap-space-600 items-center',
  protectionText: 'flex flex-col gap-space-400 flex-1',
  protectionImage: 'flex-1 overflow-hidden max-w-137.25',
  faqSection: 'w-full bg-(--color-bg-main-container) py-space-700 px-space-500 pb-space-800',
  faqContent: 'max-w-4xl mx-auto flex flex-col items-center gap-space-500',
  supportBox:
    'flex w-full p-space-400 flex-col items-start rounded-[8px] border border-(--color-card-border-neutral) bg-(--color-bg-main)',
  faqPlaceholder:
    'w-full border border-(--color-card-border-neutral) rounded-radius-200 p-space-500',
};

type LandingPageProps = {
  heroBackground: string;
  brandImage: string;
  environment?: string | undefined;
  brandCode?: string | undefined;
};

export function ModelSerialLanding({ heroBackground, brandImage, environment }: LandingPageProps) {
  const { session } = useAppState();
  const formData = useApplianceFormData();
  const applianceModelResults = useApplianceLookupResults();
  const urlParams = useUrlParams();
  const posthog = usePostHog();
  const [waitForSearchError, setWaitForSearchError] = useState(false);
  const [showSupportBox, setShowSupportBox] = useState(false);
  const [displaySideDraw, setDisplaySideDraw] = useState(false);
  const displayGiftMessage = useFeatureFlagEnabled(PosthogFeatureFlags.GIFT_MESSAGE);
  const callUsLandingFlag = posthog.getFeatureFlag(PosthogFeatureFlags.CALL_US_LANDING_PAGE);
  const callUsLandingVariant = isCallUsLandingPageVariant(callUsLandingFlag)
    ? callUsLandingFlag
    : undefined;
  const showCallUsLandingPage = callUsLandingVariant === CallUsLandingPageVariant.CALLUS;

  const brandFriendlyName = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const brandCode = (import.meta.env.VITE_BRAND_CODE as string)?.trim().toLowerCase();
  const { modelHintText, serialHintText } = getModelSerialExamples(
    import.meta.env.VITE_BRAND_CODE || brandFriendlyName,
  );
  const flagResponse = posthog.getFeatureFlag(PosthogFeatureFlags.CALL_US_LANDING_PAGE);
  const experimentVariant = isCallUsLandingPageVariant(flagResponse) ? flagResponse : undefined;
  const experimentName = experimentVariant ? PosthogFeatureFlags.CALL_US_LANDING_PAGE : undefined;
  const canadaUrl = getRegistriaUrl(brandCode, environment ?? '');
  const systemCode = getSystemCode(urlParams?.rs);
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();
  const benefitsList = [
    'Accidental damage protection — for drops, spills, and mishaps',
    'Unlimited repairs with no surprise costs',
    "Replacement appliance if yours can't be fixed",
  ];

  // Page view tracking
  useEffect(() => {
    gaPageView({
      event: 'spa-pageview',
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      experimentName,
      experimentVariant,
    });
    capturePostHogEvent(posthog, 'landing-page-viewed', store.get());
  }, []);

  const { setQuery, results, loading } = useQuery<ModelNumberLookupResponse>(queryModelNumber, {
    minQueryLength: 3,
  });

  useEffect(() => {
    if (!results?.appliances) return;
    store.send({
      type: StoreEvent.SAVE_APPLIANCE_MODEL_RESULTS,
      data: results.appliances,
    });
  }, [results]);

  const { formValues, errors, handleChange, validateAll, setFormValues } = useFormValidation(
    {
      modelNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] || '',
      serialNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] || '',
    },
    {
      modelNumber: [
        formValidators.required('Model number is required'),
        formValidators.alphanumeric('Please enter a valid model number'),
        formValidators.maxLength(30, 'Model number is too long'),
      ],
      serialNumber: [
        formValidators.required('Serial number is required'),
        formValidators.minLength(9, 'Serial number must be at least 9 characters'),
        formValidators.maxLength(20, 'Serial number must be at most 20 characters'),
        formValidators.alphanumeric('Please enter a valid serial number'),
      ],
    },
  );

  useEffect(() => {
    setFormValues({
      modelNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] || '',
      serialNumber: formData[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] || '',
    });
  }, [formData, setFormValues]);

  const handleSupportBoxToggle = () => {
    setShowSupportBox(!showSupportBox);
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'No Model/Serial Number',
      eventLabel: 'No Model/Serial CTA Clicked',
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id,
      experimentName,
      experimentVariant,
    });
    capturePostHogEvent(posthog, 'landing-no-model-serial-cta-clicked', store.get());
  };

  const toggleFindModelNumberPanel = () => {
    setDisplaySideDraw((prev) => !prev);
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: displaySideDraw ? 'Close clicked' : 'CTA clicked',
      eventLabel: 'How to find your model number',
      pagename: 'olr-landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id,
      experimentName,
      experimentVariant,
    });
  };

  const handleContinue = () => {
    if (loading) {
      setWaitForSearchError(true);
      return;
    }

    const isValid = validateAll();

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: !isValid ? 'failed' : 'success',
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory: applianceCategory,
      applianceName: applianceName,
      appSessionID: session?.id,
      experimentName,
      experimentVariant,
    });
    capturePostHogEvent(posthog, 'landing-continue-cta-clicked', store.get());

    if (!isValid) return;

    // Track successful model/serial submission
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'serialNumber',
      eventLabel: formValues.serialNumber,
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      experimentName,
      experimentVariant,
    });
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'modelNumber',
      eventLabel: formValues.modelNumber,
      pagename: 'landing',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      experimentName,
      experimentVariant,
    });

    // Save model/serial data via NEXT — store will auto-fill appliance details if matched
    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.MODEL_SERIAL]: {
          [FormKey.MODEL_NUMBER]: formValues.modelNumber,
          [FormKey.SERIAL_NUMBER]: formValues.serialNumber,
        },
      },
    });
  };

  const modelSelectOptions = (results?.appliances || applianceModelResults)?.map((appliance) => ({
    label: appliance.modelNumber,
    value: appliance.modelNumber,
    description: appliance.applianceName,
  }));

  const handleModelSelect = (value: string | null) => {
    setWaitForSearchError(false);
    handleChange({ target: { name: 'modelNumber', value: value ?? '' } });
  };

  const handleModelInputChange = (value: string) => {
    setWaitForSearchError(false);
    setQuery(value);
    handleChange({ target: { name: 'modelNumber', value } });
  };

  const generateOptions = () => {
    if (modelSelectOptions?.some((option) => option.value === formValues.modelNumber)) {
      return modelSelectOptions;
    }
    if (formValues.modelNumber.length < 3 || errors.modelNumber) {
      return [];
    }
    return [
      ...(modelSelectOptions || []),
      { label: formValues.modelNumber, value: formValues.modelNumber },
    ];
  };

  const giftMessage = displayGiftMessage ? (
    <GiftMessage message="Select cooktops qualify for a free gift with accurate model & serial numbers." />
  ) : undefined;

  const formProps: ModelSerialLandingFormProps = {
    modelNumber: formValues.modelNumber,
    serialNumber: formValues.serialNumber,
    errors: {
      modelNumber: errors.modelNumber,
      serialNumber: errors.serialNumber,
    },
    loading,
    modelHintText,
    serialHintText,
    modelOptions: generateOptions(),
    waitForSearchError,
    onModelInputChange: handleModelInputChange,
    onModelSelect: handleModelSelect,
    onSerialChange: handleChange,
    onOpenModelHelp: toggleFindModelNumberPanel,
    onContinue: handleContinue,
    giftMessage,
  };

  return (
    <div className="flex flex-col items-center gap-space-800">
      <div className={styles.heroSection} style={{ backgroundImage: `url(${heroBackground})` }}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          {showCallUsLandingPage ? (
            <ModelSerialLandingCallUs
              systemCode={systemCode}
              experimentName={experimentName}
              experimentVariant={experimentVariant}
              brandFriendlyName={brandFriendlyName}
              formProps={formProps}
            />
          ) : (
            <div className={styles.heroBox}>
              <div className={styles.heroRow}>
                <div className={styles.heroLeft}>
                  <h1 className="text-h1">Register your appliance in a few simple steps</h1>
                  <p className="text-body-large">
                    Enter your model and serial number for a quick registration
                  </p>
                </div>

                <div className={styles.heroRight}>
                  <ModelSerialLandingOnlineFormDefault {...formProps} />
                </div>
              </div>
              {giftMessage}
              <div className={styles.defaultCtaRow}>
                <div className={styles.defaultCtaContainer}>
                  <Button onClick={handleContinue}>Continue</Button>
                </div>
              </div>
              <div className="text-center -mt-2">
                <TextLink onClick={handleSupportBoxToggle}>
                  Missing your model or serial number?
                </TextLink>
              </div>
              {showSupportBox && (
                <div className={styles.supportBox}>
                  <p className="text-body">
                    If your model or serial numbers aren&apos;t recognized or you don&apos;t have
                    them to hand, call us at{' '}
                    <a href="tel:+18335522314" className="text-body-link-strong">
                      +1 833-552-2314
                    </a>{' '}
                    for support.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {canadaUrl && (
        <div className={styles.canadaLink}>
          <a
            href={canadaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-body-strong text-text-link-default underline"
          >
            I am registering my appliance in Canada
          </a>
        </div>
      )}
      <div className={styles.benefitsSection}>
        <RegBenefits heading="Why register your appliance?" benefits={landingBenefits} />
      </div>
      <div className={styles.protectionSection}>
        <div className={styles.protectionContent}>
          <div className={styles.protectionText}>
            <h2 className="text-h2">
              Go beyond the standard warranty. Get additional protection with Appliance Care
            </h2>
            <p className="text-body">
              Your manufacturer's warranty is a great start, but an Appliance Care protection plan
              goes further to keep you covered:
            </p>
            <ul className="flex flex-col gap-space-300">
              {benefitsList.map((item) => (
                <li key={item} className="flex items-start gap-space-200">
                  <CircleTickSolid className="shrink-0 mt-space-100" />
                  <span className="text-body">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-body">
              You can add Appliance Care as part of your registration journey — it's quick, easy,
              and gives you peace of mind.
            </p>
          </div>

          <div className={styles.protectionImage}>
            <img
              src={brandImage}
              alt={`${brandFriendlyName} kitchen`}
              className="w-full h-auto object-cover"
              width={549}
              height={366}
            />
          </div>
        </div>
      </div>

      <div className={styles.faqSection}>
        <div className={styles.faqContent}>
          <h2 className="text-h2 text-center">Your questions</h2>
          <p className="text-body text-center">We're always here to help.</p>
          <Accordion title="What do I need to register my appliance?">
            You&apos;ll need the model and serial numbers, the approximate purchase price, and the
            delivery date.
          </Accordion>
          <Accordion title="Where can I find my model and serial numbers?">
            You can locate your model and serial numbers on the appliance, within product
            documentation, or within information provided after your purchase.
          </Accordion>
        </div>
      </div>
      {modelNumberLocationContent && modelNumberLocationOptions && (
        <SlidingPanel
          isOpen={displaySideDraw}
          onClose={toggleFindModelNumberPanel}
          height="fit-content"
        >
          <ModelNumberLocation
            options={getModelNumberLocationOptions(brandCode)}
            content={modelNumberLocationContent}
            isOpen={displaySideDraw}
            handleClose={toggleFindModelNumberPanel}
          />
        </SlidingPanel>
      )}
    </div>
  );
}
